import React, { useState, useMemo, useCallback, useRef } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { Box, Flex, ScrollArea, Text } from '@mantine/core';
import { NewTable } from '../NewTable';
import { TbChevronDown, TbChevronUp } from 'react-icons/tb';
import { useReportContext } from '../../context/ReportContext';
import { SectionKeyContext } from '../../context/SectionKeyContext';
import { useSearchParams } from 'react-router-dom';
import { REPORT_TYPE_IDS } from '../../consts/v1/reportTypes';
import { idCreator } from '../../../../Project/utils/idCreator';

const SECTION_HEADER_BUTTON_SX = { position: 'sticky', left: 0 };

function VirtuosoSectionHeaderRow({ row, expandedSections, toggleSection, sectionRefs }) {
  const sectionName = row.section?.name;
  const sectionKey = row.key;
  const isOpen = expandedSections.has(sectionKey);

  return (
    <div ref={el => (sectionRefs.current[sectionKey] = el)}>
      {sectionName && (
        <Flex
          id={`formpf-sections-section-toggle-${idCreator(sectionKey)}`}
          component="button"
          align="center"
          gap="10px"
          mb="16px"
          p="0"
          onClick={() => toggleSection(sectionKey)}
          sx={SECTION_HEADER_BUTTON_SX}
        >
          <Text
            id={`formpf-sections-section-label-${idCreator(sectionKey)}`}
            fz="16px"
            c="#495057"
            lh="22px"
          >
            {sectionName}
          </Text>
          {isOpen ? <TbChevronUp size="16" /> : <TbChevronDown size="16" />}
        </Flex>
      )}
    </div>
  );
}

function VirtuosoQuestionRow({
  row,
  expandedQuestions,
  sectionTables,
  isMultiFunds,
  toggleQuestion,
  effectiveFunds
}) {
  const { currentDataKey, isComparisonMode, onChangeFund, schema, addRow, onDeleteRow } =
    useReportContext();

  const { question, sectionKey, itemKey } = row;

  const qKey = row.key;
  const isQuestionOpen = expandedQuestions.has(qKey);

  const tableNames = useMemo(
    () => question.tableNames || [question.tableName],
    [question.tableNames, question.tableName]
  );

  const tablesOverflowX = useMemo(
    () => (tableNames.some(tableName => sectionTables[tableName]?.virtualized) ? 'hidden' : 'auto'),
    [tableNames, sectionTables]
  );

  const tablesContainerSx = useMemo(
    () => ({
      overflowX: tablesOverflowX
    }),
    [tablesOverflowX]
  );

  return (
    <Box w="100%" key={qKey} mb="16px" pl="16px">
      <ScrollArea direction="column" gap="16px" sx={tablesContainerSx} offsetScrollbars>
        <SectionKeyContext.Provider value={sectionKey}>
          {tableNames.map((tableName, tableIndex) => (
            <NewTable
              key={tableName}
              funds={effectiveFunds}
              fundId={currentDataKey}
              isMultiFunds={isComparisonMode ? true : isMultiFunds}
              groupColumn={sectionTables[tableName].groupColumn}
              tableName={tableName}
              onChangeFund={onChangeFund}
              schema={schema[tableName]}
              tableType={sectionTables[tableName].type}
              name={question?.name}
              onAddColumn={addRow}
              onDeleteColumn={onDeleteRow}
              questionLevels={sectionTables[tableName].questionLevels}
              rankLevels={sectionTables[tableName]?.rankLevels}
              levels={sectionTables[tableName]?.levels}
              subGroupColumn={sectionTables[tableName]?.subGroupColumn}
              commonColumn={sectionTables[tableName]?.commonColumn}
              virtualized={sectionTables[tableName]?.virtualized}
              showQuestionHeader={tableIndex === 0}
              isQuestionOpen={isQuestionOpen}
              onToggleQuestion={() => toggleQuestion(sectionKey, itemKey, row.questionIndex)}
            />
          ))}
        </SectionKeyContext.Provider>
      </ScrollArea>
    </Box>
  );
}

export const Sections = ({
  targetRef,
  isMultiFunds,
  sectionRefs,
  virtuosoRef,
  onChangeActiveSection,
  onChangeActiveQuestion,
  sections,
  sectionTables,
  expandedSections: controlledExpandedSections,
  expandedQuestions: controlledExpandedQuestions,
  onToggleSection,
  onToggleQuestion,
  isStaticTemplate
}) => {
  const { funds, currentDataKey, isComparisonMode } = useReportContext();
  const [searchParams] = useSearchParams();
  const reportType = searchParams.get('type');
  const [uncontrolledExpandedSections, setUncontrolledExpandedSections] = useState(() => new Set());
  const [uncontrolledExpandedQuestions, setUncontrolledExpandedQuestions] = useState(
    () => new Set()
  );

  const expandedSections = controlledExpandedSections ?? uncontrolledExpandedSections;
  const expandedQuestions = controlledExpandedQuestions ?? uncontrolledExpandedQuestions;

  const effectiveFunds = useMemo(() => {
    if (!isComparisonMode) return funds;

    if (isMultiFunds) {
      return Object.values(funds || {}).reduce((acc, fund) => {
        if (!fund) return acc;
        if (fund.previous_fund?.fund_id) {
          acc[fund.previous_fund.fund_id] = {
            ...fund.previous_fund,
            fund_name: `${fund.previous_fund.fund_name} (${fund.previous_fund.period}, ${fund.previous_fund.year})`
          };
        }
        acc[fund.fund_id] = {
          ...fund,
          fund_name: `${fund.fund_name} (${fund.period}, ${fund.year})`
        };

        delete acc[fund.fund_id].previous_fund;
        return acc;
      }, {});
    }

    const selectedFund = funds?.[currentDataKey];

    if (!selectedFund) return funds;

    const previousFund = selectedFund.previous_fund;
    const isAIF001 = currentDataKey === REPORT_TYPE_IDS.AIF001;
    return {
      ...(previousFund?.fund_id || isAIF001
        ? {
            [previousFund.fund_id]: {
              ...previousFund,
              fund_name: isAIF001
                ? `${previousFund.period}, ${previousFund.year}`
                : `${previousFund.fund_name} (${previousFund.period}, ${previousFund.year})`
            }
          }
        : {}),
      [currentDataKey]: {
        ...selectedFund,
        fund_name: isAIF001
          ? `${selectedFund.period}, ${selectedFund.year}`
          : `${selectedFund.fund_name} (${selectedFund.period}, ${selectedFund.year})`
      }
    };
  }, [funds, currentDataKey, isComparisonMode, isMultiFunds]);

  const lastEmittedSectionKeyRef = useRef(null);
  const lastEmittedQuestionKeyRef = useRef(null);

  const toggleSection = useCallback(
    sectionKey => {
      if (onToggleSection) return onToggleSection(sectionKey);
      setUncontrolledExpandedSections(prev => {
        const next = new Set(prev);
        if (next.has(sectionKey)) next.delete(sectionKey);
        else next.add(sectionKey);
        return next;
      });
    },
    [onToggleSection]
  );

  const toggleQuestion = useCallback(
    (sectionKey, _itemKey, questionIndex) => {
      const questionKey = `${sectionKey}::q-${questionIndex}`;
      if (onToggleQuestion) return onToggleQuestion(sectionKey, questionIndex);
      setUncontrolledExpandedQuestions(prev => {
        const next = new Set(prev);
        if (next.has(questionKey)) next.delete(questionKey);
        else next.add(questionKey);
        return next;
      });
    },
    [onToggleQuestion]
  );

  const flatRows = useMemo(() => {
    const rows = [];
    sections.forEach((section, sIdx) => {
      const sectionKey = section.key || section.name || `section-${sIdx}`;
      rows.push({
        type: 'section',
        key: sectionKey,
        sectionIndex: sIdx,
        section
      });

      if (!expandedSections.has(sectionKey)) return;

      const questionsByName = new Map();
      (section.questions || []).forEach(q => {
        const name = q?.name || 'Question';
        if (!questionsByName.has(name)) {
          questionsByName.set(name, { name, tableNames: [] });
        }
        questionsByName.get(name).tableNames.push(q.tableName);
      });

      const mergedQuestions = Array.from(questionsByName.values());
      mergedQuestions.forEach((mergedQuestion, qIdx) => {
        rows.push({
          type: 'question',
          key: `${sectionKey}::q-${qIdx}`,
          sectionIndex: sIdx,
          questionIndex: qIdx,
          section,
          question: mergedQuestion,
          sectionKey,
          itemKey: sectionKey
        });
      });
    });
    return rows;
  }, [sections, expandedSections]);

  const onChangeIndex = useCallback(
    value => {
      if (!flatRows.length) return;

      const start = value?.startIndex ?? 0;
      const end = value?.endIndex ?? start;
      const lastIndex = flatRows.length - 1;

      let pivotIndex;

      if (start <= 0) {
        pivotIndex = 0;
      } else if (end >= lastIndex) {
        pivotIndex = lastIndex;
      } else {
        pivotIndex = Math.round((start + end) / 2);
      }

      const clampedIndex = Math.max(0, Math.min(pivotIndex, lastIndex));
      const row = flatRows[clampedIndex];

      const activeSection = sections[row.sectionIndex];
      if (activeSection) {
        const sectionEmitKey =
          activeSection.key ?? activeSection.name ?? `section-${row.sectionIndex}`;
        if (sectionEmitKey !== lastEmittedSectionKeyRef.current) {
          lastEmittedSectionKeyRef.current = sectionEmitKey;
          onChangeActiveSection?.(activeSection);
        }
      }

      let activeQuestionRow = null;

      if (start <= 0) {
        for (let i = 0; i <= lastIndex; i += 1) {
          if (flatRows[i].type === 'question') {
            activeQuestionRow = flatRows[i];
            break;
          }
        }
      } else if (end >= lastIndex) {
        for (let i = lastIndex; i >= 0; i -= 1) {
          if (flatRows[i].type === 'question') {
            activeQuestionRow = flatRows[i];
            break;
          }
        }
      } else {
        const middle = Math.round((start + end) / 2);
        const maxOffset = Math.max(middle - start, end - middle);

        for (let offset = 0; offset <= maxOffset; offset += 1) {
          const forward = middle + offset;
          const backward = middle - offset;

          if (forward >= start && forward <= end && flatRows[forward]?.type === 'question') {
            activeQuestionRow = flatRows[forward];
            break;
          }

          if (backward >= start && backward <= end && flatRows[backward]?.type === 'question') {
            activeQuestionRow = flatRows[backward];
            break;
          }
        }
      }

      if (activeQuestionRow) {
        const qKey = activeQuestionRow.key;
        if (qKey !== lastEmittedQuestionKeyRef.current) {
          lastEmittedQuestionKeyRef.current = qKey;
          onChangeActiveQuestion?.({
            sectionKey: activeQuestionRow.sectionKey,
            questionIndex: activeQuestionRow.questionIndex,
            questionKey: qKey
          });
        }
      }
    },
    [flatRows, sections, onChangeActiveSection, onChangeActiveQuestion]
  );

  const renderRow = useCallback(
    index => {
      const row = flatRows[index];
      if (!row) return null;

      if (row.type === 'section') {
        return (
          <VirtuosoSectionHeaderRow
            row={row}
            expandedSections={expandedSections}
            toggleSection={toggleSection}
            sectionRefs={sectionRefs}
          />
        );
      }

      return (
        <VirtuosoQuestionRow
          row={row}
          expandedQuestions={expandedQuestions}
          sectionTables={sectionTables}
          isMultiFunds={isMultiFunds}
          toggleQuestion={toggleQuestion}
          effectiveFunds={effectiveFunds}
        />
      );
    },
    [
      flatRows,
      expandedSections,
      expandedQuestions,
      sectionTables,
      isMultiFunds,
      sectionRefs,
      toggleSection,
      toggleQuestion,
      effectiveFunds
    ]
  );

  return (
    <div
      ref={targetRef}
      style={{
        height: isMultiFunds
          ? isStaticTemplate
            ? 'calc(100% - 54px)'
            : 'calc(100% - 24px)'
          : reportType === REPORT_TYPE_IDS.AIF002
            ? `calc(100% - ${isStaticTemplate ? 115 : 77}px)`
            : '100%'
      }}
    >
      <Virtuoso
        style={{
          height: isMultiFunds ? `calc(100% - ${isStaticTemplate ? 30 : 15}px)` : '100%',
          width: '100%',
          overflowX: 'hidden'
        }}
        totalCount={flatRows.length}
        rangeChanged={onChangeIndex}
        itemContent={renderRow}
        overscan={1}
        ref={virtuosoRef}
      />
    </div>
  );
};
